<script setup lang="ts" generic="T">
import {useI18n} from "vue-i18n";
import {inject, PropType, ref, watch, computed, nextTick} from "vue";
import {injectKeyFormData, injectKeyUpdateFormData} from "/@src/constants/keys";
import {getNestedField} from "/@src/utils";
import BaseFormField, {AddonConfig, FormFieldLayout} from "/@src/components/custom/form/BaseFormField.vue";
import {useFormField} from "/@src/composable/form/useFormField";
import {Transform} from "/@src/components/custom/form/BasePanelForm.vue";
import {toISOLocal} from "/@src/utils/date";

export type FormTimeType = 'date'|'time'|'datetime'

const props = defineProps({
  label: String,
  field: {
    type: String,
  },
  required: Boolean,
  loading: Boolean,
  ignoreFieldUndefined: Boolean,
  placeholder: String,
  modelValue: {
    type: String,
  },
  disabled: Boolean,
  mode:{
    type: String as PropType<FormTimeType>,
    default: 'datetime',
  },
  layout: {
    type: String as PropType<FormFieldLayout>,
  },
  index: Number,
  //是否显示icon
  isIcon: {
    type: Boolean,
    default: true
  },
  transform: {
    type: Object as PropType<Transform<T,string>>
  },
  labelAddonConfig:{
    type: Object as PropType<AddonConfig>,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void,
  (e: 'onChange', value: string): void
}>()

const { t } = useI18n()
const localModel = ref(props.modelValue)
const localDate = ref(props.modelValue?new Date(props.modelValue):undefined)
const refBase = ref<InstanceType<typeof BaseFormField>>()

const fieldRef = ref(props.field)
watch(()=>props.field,(newVal)=>{
  fieldRef.value = newVal
})

watch(localDate,(newVal)=>{
  if (newVal){
    localModel.value = toISOLocal(newVal)
  }else {
    localModel.value = ''
  }
  emit('onChange',localModel.value)
})

watch(localModel,(newVal)=>{
  if (newVal) {
    localDate.value = new Date(newVal)
  }
})

useFormField<string>(localModel,fieldRef,{
  transform:props.transform,
  index: props.index,
  deepWatch: true,
  ignoreFieldUndefined: props.ignoreFieldUndefined,
  only:true,
  dataWatcher:(newVal) => {
    emit('update:modelValue', newVal)
  }
})

const icon = computed(() => {
  if (props.isIcon) {
    return 'feather:calendar'
  } else {
    return ''
  }
})

// 测试，适配disabled等状态
const textColor = computed(() => {
  if (props.disabled) {
    return '#a1a1aa'
  } else {
    return ''
  }
})
const textColorDark = computed(() => {
  if (props.disabled) {
    return '#52525b'
  } else {
    return ''
  }
})

</script>

<template>
  <base-form-field
      class="form-field"
      ref="refBase"
      :layout="layout"
      :field="field"
      :label="label"
      :model-value="localModel"
      :addon-config="labelAddonConfig"
    >
    <v-date-picker :popover="!disabled"  v-model="localDate" color="green"  :mode="mode" is24hr>
      <template #default="{ inputValue, inputEvents }">
        <V-Field class="mb-0">
          <V-Control :icon="icon">
            <input
              class="input h-[38px] pl-9 disabled:bg-[#F5F5F5] dark:disabled:text-gray-400 bg-[hsl(0, 0%, 96%)] dark:bg-[var(--dark-sidebar-light-2)] dark:disabled:bg-[var(--dark-sidebar-light-10)]"
              type="text"
              :disabled="disabled"
              :placeholder="placeholder || t('placeholder-select-date')"
              :value="inputValue"
              v-on="inputEvents"
            />
          </V-Control>
        </V-Field>
      </template>
    </v-date-picker>
  </base-form-field>
</template>
<style lang="scss">
.form-field{
  .form-icon{
    svg{
      color: v-bind(textColor) !important;
    }
  }
}
.is-dark{
  .form-field{
    .form-icon{
      svg{
        color: v-bind(textColorDark) !important;
      }
    }
  }
}
</style>
